import {
  Button,
  Card,
  CodeBlock,
  Divider,
  HStack,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

import { usePageEvent } from '../analytics/usePageEvent';

const NPM_PACKAGE = 'n8n-nodes-vectime';
const NPM_URL = `https://www.npmjs.com/package/${NPM_PACKAGE}`;

const INSTALL = `# Self-hosted n8n
cd ~/.n8n/custom          # create the folder if it does not exist
npm install ${NPM_PACKAGE}

# then restart n8n — the node appears as "VecTime"`;

const ROWS_INPUT = `[
  { "date": "2026-01-01", "sku": "A-100", "units": 412 },
  { "date": "2026-02-01", "sku": "A-100", "units": 430 },
  { "date": "2026-01-01", "sku": "B-200", "units": 92  },
  { "date": "2026-02-01", "sku": "B-200", "units": 88  }
]`;

const ARRAY_INPUT = `[
  { "sku": "A-100", "series": [412, 430, 455, 471, 502, 538] },
  { "sku": "B-200", "series": [92, 88, 95, 101, 97, 104] }
]`;

const SPLIT_OUTPUT = `{
  "seriesIndex": 0,
  "group": "A-100",
  "step": 1,
  "date": "2026-07-01",
  "point": 561.2,
  "q10": 498.1,
  "q50": 561.2,
  "q90": 624.8
}`;

const SERIES_OUTPUT = `{
  "seriesIndex": 0,
  "group": "A-100",
  "model": "timesfm-2.5-200m",
  "horizon": 3,
  "point": [561.2, 583.4, 601.9],
  "dates": ["2026-07-01", "2026-08-01", "2026-09-01"],
  "quantiles": {
    "q10": [498.1, 505.7, 511.2],
    "q50": [561.2, 583.4, 601.9],
    "q90": [624.8, 661.0, 692.4]
  }
}`;

const OPTIONS: Array<{ name: string; value: string; what: string }> = [
  {
    name: 'Date Field',
    value: '—',
    what: 'Sorts each series by date before forecasting, and labels every forecast step with the date it lands on. Frequency is inferred from the median gap, or set it by hand.',
  },
  {
    name: 'Keep Last N Points',
    value: 'all',
    what: 'Trims each series to its most recent N observations, so a long history stays inside your plan’s context window.',
  },
  {
    name: 'Max Series per Request',
    value: 'auto',
    what: 'How many series ride in one call. Left at 0 the node reads your plan’s own ceiling from the API — an unmetered lookup — and batches to it.',
  },
  {
    name: 'Split Points Into Items',
    value: 'off',
    what: 'One item per forecast step with the quantiles as flat fields — the shape to append straight into a sheet or a table.',
  },
  {
    name: 'Simplify',
    value: 'on',
    what: 'Turn it off to also receive quota usage, request ID and latency on every item.',
  },
  {
    name: 'Cold Start Retries',
    value: '3',
    what: 'Retries while the model loads. Only a 503 is retried — never a call that was already billed.',
  },
];

export function IntegrationN8nPage() {
  usePageEvent('integration_viewed', { integration: 'n8n' });

  return (
    <div className="shell section-block">
      <VStack gap={5}>
        <VStack gap={2}>
          <span className="eyebrow">Integration</span>
          <Heading level={1} className="display--md">
            Forecasting <span className="gradient-text">in n8n.</span>
          </Heading>
          <p className="lede measure--wide">
            A community node that turns rows in a workflow into a forecast with a confidence band.
            Point it at the column holding your numbers, say how far ahead to look, and write the
            result back wherever it came from. No Python, no model to host, no training step.
          </p>
          <HStack gap={1.5} wrap="wrap">
            <Button label="Get a free API key" variant="primary" href="/register" />
            <Button label="View on npm" variant="secondary" href={NPM_URL} />
          </HStack>
          <Text size="sm" color="secondary">
            The free tier is 300 forecasts a month on the real TimesFM 2.5 weights, no card.
          </Text>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <Heading level={2}>Install</Heading>
          <Text color="secondary">
            On self-hosted n8n, install it from Settings → Community nodes, or from the CLI:
          </Text>
          <CodeBlock code={INSTALL} language="bash" hasCopyButton container="card" />
          <Text color="secondary">
            Then add a <strong>VecTime API</strong> credential and paste a key from your dashboard.
            Testing the credential calls <span className="mono">GET /v1/models</span>, which is
            authenticated but unmetered — it costs no quota.
          </Text>
          <Card padding={2} variant="muted">
            <Text size="sm" color="secondary">
              n8n Cloud only installs verified community nodes. Until this one is verified there, use
              the HTTP Request node against{' '}
              <span className="mono">https://api.vectime.cloud/v1/forecast</span> — the payload is in
              the <a href="/docs">API reference</a>.
            </Text>
          </Card>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <Heading level={2}>It reads both shapes a workflow carries</Heading>
          <Text color="secondary">
            <span className="measure--wide">
              A time series arrives either as one item per observation or as an array already
              assembled. The node takes either, and in both cases every series in the run travels to
              the model in a single batched call.
            </span>
          </Text>
          <div className="split">
            <VStack gap={1}>
              <Text size="sm" weight="semibold" color="secondary">
                ROWS INTO ONE SERIES
              </Text>
              <CodeBlock code={ROWS_INPUT} language="json" hasCopyButton container="card" />
              <Text size="sm" color="secondary">
                What Sheets, Postgres and paginated APIs produce. Set <em>Value Field</em> to{' '}
                <span className="mono">units</span> and <em>Group Field</em> to{' '}
                <span className="mono">sku</span>, and each SKU becomes its own series.
              </Text>
            </VStack>
            <VStack gap={1}>
              <Text size="sm" weight="semibold" color="secondary">
                ONE ARRAY PER ITEM
              </Text>
              <CodeBlock code={ARRAY_INPUT} language="json" hasCopyButton container="card" />
              <Text size="sm" color="secondary">
                What an HTTP Request or a Code node usually hands over. Set <em>Series Field</em> to{' '}
                <span className="mono">series</span>; every item becomes one series.
              </Text>
            </VStack>
          </div>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <Heading level={2}>What comes back</Heading>
          <Text color="secondary">
            <span className="measure--wide">
              One item per series by default, or one item per forecast step with{' '}
              <em>Split Points Into Items</em> — the shape that appends straight into a sheet.
              Whichever you pick, <span className="mono">point</span> is the median and the band
              between <span className="mono">q10</span> and <span className="mono">q90</span> is the
              forecast&apos;s own statement of how sure it is. Safety stock, alert thresholds and
              capacity buffers belong on the band, not on the median.
            </span>
          </Text>
          <div className="split">
            <VStack gap={1}>
              <Text size="sm" weight="semibold" color="secondary">
                PER SERIES
              </Text>
              <CodeBlock code={SERIES_OUTPUT} language="json" hasCopyButton container="card" />
            </VStack>
            <VStack gap={1}>
              <Text size="sm" weight="semibold" color="secondary">
                PER FORECAST STEP
              </Text>
              <CodeBlock code={SPLIT_OUTPUT} language="json" hasCopyButton container="card" />
            </VStack>
          </div>
        </VStack>

        <Divider />

        <VStack gap={2}>
          <Heading level={2}>Options worth knowing</Heading>
          <Table density="balanced" dividers="rows">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Option</TableHeaderCell>
                <TableHeaderCell>Default</TableHeaderCell>
                <TableHeaderCell>What it is for</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {OPTIONS.map((option) => (
                <TableRow key={option.name}>
                  <TableCell>{option.name}</TableCell>
                  <TableCell>
                    <span className="mono">{option.value}</span>
                  </TableCell>
                  <TableCell>
                    <Text size="sm" color="secondary">
                      {option.what}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </VStack>

        <VStack gap={2}>
          <Heading level={2}>Quota, batching and failures</Heading>
          <Text color="secondary">
            <span className="measure--wide">
              Quota counts calls, not series: twelve SKUs batched into one request cost one of your
              monthly forecasts. The node batches by default, so <em>Max Series per Request</em> is
              what decides the price of a large run — and it keeps you clear of the per-minute rate
              limit at the same time. A call that fails is not billed, and a cold model answers a
              retryable <span className="mono">503</span> that the node handles itself.
              Turn on n8n&apos;s <em>Continue On Fail</em> and a refusal arrives as an item instead of
              stopping the workflow.
            </span>
          </Text>
          <Card padding={3} variant="muted">
            <VStack gap={1.5}>
              <Text weight="semibold">Start with the example workflow</Text>
              <Text size="sm" color="secondary">
                The package ships one: 24 months of history for three SKUs, six months forecast for
                all three in a single call, one row out per SKU per month. Import it, attach a
                credential, run it — three of your 300 free forecasts.
              </Text>
              <HStack gap={1.5} wrap="wrap">
                <Button label="Create a free API key" variant="primary" size="sm" href="/register" />
                <Button label="Read the API reference" variant="secondary" size="sm" href="/docs" />
              </HStack>
            </VStack>
          </Card>
        </VStack>
      </VStack>
    </div>
  );
}
